import { ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  SxProps,
  Theme,
  alpha,
} from '@mui/material';

import {
  RESULT_ENTRY_DURATION_MS,
  RESULT_ENTRY_EASING,
  resultEntryDelayMs,
} from '../resultMotion';
import { RESULT_MEDIA_WIDTH } from './ResultMedia';

/**
 * La columna de decisión. Es LA invariante del sistema: el importe que se
 * compara y el botón que actúa caen en la misma x en todas las verticales, así
 * el asesor compara un ferry contra un tren sin releer cada card.
 */
export const DECISION_COLUMN_WIDTH = 224;

interface Props {
  /**
   * Arquetipo PRODUCTO: la imagen es argumento de venta (vehículo,
   * experiencia, circuito). Sin `media` el layout es el arquetipo HORARIOS y
   * la columna no existe — no queda un rectángulo gris donde iría la foto.
   */
  media?: ReactNode;
  header?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  details?: ReactNode;
  children?: ReactNode;
  price: ReactNode;
  action?: ReactNode;
  /**
   * Franja de tarifas: ancho completo debajo de las columnas, con su propio
   * botón por fila. Cuando hay franja, la columna de decisión muestra el
   * "desde" y NO lleva CTA — el CTA baja a cada tarifa.
   */
  fares?: ReactNode;
  /** Posición en la lista: de ahí sale el escalonado de la entrada. */
  index?: number;
  sx?: SxProps<Theme>;
}

/**
 * Layout maestro para resultados de Rutero. Cada vertical traduce sus datos a
 * estos slots y elige su arquetipo con la presencia de `media`; lo que nunca
 * cambia es dónde está el precio.
 */
export function RuteroResultCard({
  media,
  header,
  title,
  subtitle,
  details,
  children,
  price,
  action,
  fares,
  index,
  sx,
}: Props) {
  const entryDelay = resultEntryDelayMs(index);

  return (
    <Card
      variant="outlined"
      sx={[
        (theme) => ({
          overflow: 'hidden',
          borderRadius: 1,
          borderColor: alpha(theme.palette.text.primary, 0.11),
          '@keyframes ruteroResultIn': {
            from: { opacity: 0, transform: 'translateY(6px)' },
            to: { opacity: 1, transform: 'none' },
          },
          '@keyframes ruteroResultFade': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          animation: `ruteroResultIn ${RESULT_ENTRY_DURATION_MS}ms ${RESULT_ENTRY_EASING} ${entryDelay}ms both`,
          // El hover mueve SOLO el borde: la sombra y el tinte de fondo de la
          // v1 hacían latir la lista entera al pasar el mouse.
          transition: 'border-color 120ms ease',
          '&:hover': {
            borderColor: alpha(theme.palette.primary.main, 0.42),
          },
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.14)}`,
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: `ruteroResultFade ${RESULT_ENTRY_DURATION_MS}ms linear ${entryDelay}ms both`,
          },
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: media
            ? {
              xs: '1fr',
              sm: `${RESULT_MEDIA_WIDTH}px minmax(0, 1fr)`,
              lg: `${RESULT_MEDIA_WIDTH}px minmax(0, 1fr) ${DECISION_COLUMN_WIDTH}px`,
            }
            : {
              xs: '1fr',
              sm: 'minmax(0, 1fr)',
              lg: `minmax(0, 1fr) ${DECISION_COLUMN_WIDTH}px`,
            },
          gap: { xs: 1.5, sm: 1.75 },
          alignItems: 'stretch',
          // 12/14 y no el padding grande de MUI: la densidad es criterio de
          // aceptación (6–7 resultados en una pantalla de 1440×900).
          p: '12px 14px',
          '&:last-child': { pb: '12px' },
          // En mobile los links de condiciones crecen: a 12px son imposibles
          // de acertar con el pulgar.
          [theme.breakpoints.down('sm')]: {
            '& .MuiLink-root': { fontSize: 13 },
          },
        })}
      >
        {/* En mobile no hay media, ni siquiera en producto: los 112px de foto
            no justifican el alto que roban en una pantalla de comparación. La
            imagen se recupera en el detalle y en la reserva. */}
        {media && (
          <Box minWidth={0} sx={{ display: { xs: 'none', sm: 'block' } }}>
            {media}
          </Box>
        )}

        <Box minWidth={0}>
          {/* La línea de identidad es texto de 11,5 px en las nueve, pero el
              tema define el interlineado en PÍXELES (24), así que no baja con
              el cuerpo: una palabra gris de 11,5 px ocupaba 26 px de card. El
              `inherit` lo ata al renglón de este bloque — vale para el slot de
              identidad, no para el resto de la card. */}
          {header && (
            <Box
              mb={0.5}
              sx={{
                fontSize: 11.5,
                lineHeight: 1.35,
                '& .MuiTypography-root': { lineHeight: 'inherit' },
              }}
            >
              {header}
            </Box>
          )}
          {title}
          {subtitle && (
            <Box mt={0.5}>
              {subtitle}
            </Box>
          )}
          {details && (
            <Box mt={0.5}>
              {details}
            </Box>
          )}
          {children && (
            <Box mt={1}>
              {children}
            </Box>
          )}
        </Box>

        {/* La fila de cierre: precio a la izquierda, CTA a la derecha, borde
            arriba. Es la columna de decisión ROTADA — la misma información en
            el mismo orden, donde el pulgar la busca. */}
        <Stack
          direction={{ xs: 'row', lg: 'column' }}
          spacing={0.75}
          justifyContent={{ xs: 'space-between', lg: 'center' }}
          alignItems={{ xs: 'center', lg: 'flex-end' }}
          sx={(theme) => ({
            gridColumn: { xs: '1', sm: '1 / -1', lg: 'auto' },
            borderTop: { xs: 1, sm: 1, lg: 0 },
            borderLeft: { xs: 0, lg: 1 },
            borderColor: 'divider',
            pl: { xs: 0, lg: 1.75 },
            pt: { xs: 1, lg: 0 },
            minHeight: { xs: 44, lg: 'auto' },
            // El CTA se agranda al tamaño que un pulgar acierta.
            '& .MuiButton-root': { minHeight: { xs: 44, lg: 'auto' } },
            // Mientras la columna está ROTADA, el precio abre la fila por la
            // izquierda: alinearlo a la derecha dejaría sus aclaraciones
            // colgando del borde equivocado. En `lg` vuelve a mandar el
            // `align` de quien lo dibuja.
            [theme.breakpoints.down('lg')]: {
              '& > .MuiBox-root': { textAlign: 'left' },
            },
          })}
        >
          {price}
          {action}
        </Stack>

        {/* La franja arranca en el borde izquierdo del contenido y llega hasta
            el derecho: las tarifas son filas de una tabla, no una tercera
            columna más. */}
        {fares && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              borderTop: 1,
              borderColor: 'divider',
              mt: 0.25,
            }}
          >
            {fares}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
